import { green, purple, red } from '@mui/material/colors';
import { LineBasicMaterial, LineDashedMaterial } from 'three';

import { CalculatedLine } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

export type ReferenceLineStatus = 'normal' | 'hovered' | 'selected' | 'highlighted';

const selectedReferenceLineColor = 'red';

const materialParameters = {
  depthTest: false,
  depthWrite: false,
  transparent: true,
} as const;

const defaultMaterial = new LineBasicMaterial(materialParameters);
const defaultDashedMaterial = new LineDashedMaterial(materialParameters);

const selectedMaterial = defaultMaterial.clone();
selectedMaterial.color.setStyle(selectedReferenceLineColor);

const referenceLineMaterialDictionary: { [key: string]: LineBasicMaterial | LineDashedMaterial } = {};

export const getReferenceLineMaterial = (line: CalculatedLine, state: ReferenceLineStatus) => {
  const colorString = getReferenceLineColor(line, state);
  const styleString = getReferenceLineMaterialStyle(line);

  const materialKey = `${colorString}-${styleString}`;

  if (materialKey in referenceLineMaterialDictionary) {
    return referenceLineMaterialDictionary[materialKey];
  }

  const material = (styleString === 'solid' ? defaultMaterial : defaultDashedMaterial).clone();
  material.color.setStyle(colorString);

  referenceLineMaterialDictionary[materialKey] = material;
  return material;
};

const getReferenceLineMaterialStyle = (line: CalculatedLine) => (line.lineType === 'Member' ? 'dashed' : 'solid');

const getReferenceLineColor = (line: CalculatedLine, state: ReferenceLineStatus) => {
  const shade = (() => {
    switch (state) {
      case 'highlighted':
        return 600;

      case 'selected':
        return 800;

      case 'hovered':
        return 400;

      case 'normal':
        return 200;

      default:
        assertExhaustiveSwitch(state);
    }
  })();

  if (state === 'highlighted') return green[shade];

  switch (line.lineType) {
    case 'Absolute':
      return purple[shade];

    case 'Parallel':
    case 'PointSlope':
    case 'PointToPoint':
    case 'Perpendicular':
    case 'Division':
    case 'Member':
      return red[shade];

    default:
      assertExhaustiveSwitch(line, 'Unrecognized line type.');
  }
};
